<template>
  <div class="ranking-page">
    <!-- Banner区域 -->
    <div class="banner">
      <div class="banner-content">
        <h1 class="title">投票广场</h1>
        <p class="subtitle">参与投票，发表观点</p>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="container">
      <!-- 分类标签页 -->
      <el-tabs v-model="activeCategory" class="ranking-tabs" @tab-change="handleTabChange">
        <el-tab-pane label="全部" name="all">
          <!-- 时间筛选 -->
          <div class="time-filter">
            <el-radio-group v-model="sortType" size="large">
              <el-radio-button label="latest">最新</el-radio-button>
              <el-radio-button label="hot">最热</el-radio-button>
              <el-radio-button label="favorite">已收藏</el-radio-button>
            </el-radio-group>
          </div>

          <!-- 投票列表 -->
          <div class="vote-grid">
            <template v-if="filteredVotes.length">
              <el-card v-for="vote in filteredVotes"
                       :key="vote.id"
                       class="vote-card"
                       @click="$router.push(`/votes/${vote.id}`)">
                <template #header>
                  <div class="card-header">
                    <h2>{{ vote.title }}</h2>
                    <el-button
                      type="primary"
                      link
                      @click.stop="toggleFavorite(vote.id)"
                      :class="{ 'is-favorite': isFavorite(vote.id) }"
                      class="favorite-btn"
                    >
                      <el-icon>
                        <StarFilled v-if="isFavorite(vote.id)" />
                        <Star v-else />
                      </el-icon>
                    </el-button>
                  </div>
                </template>
                <div class="vote-info">
                  <p>{{ vote.description }}</p>
                  <div class="vote-stats">
                    <span>总票数: {{ vote.totalVotes }}</span>
                    <span>截止时间: {{ formatDate(vote.endTime) }}</span>
                  </div>
                </div>
              </el-card>
            </template>
            <el-empty v-else description="暂无数据" />
          </div>
        </el-tab-pane>

        <el-tab-pane v-for="category in categories"
                     :key="category.value"
                     :label="category.label"
                     :name="category.value">
          <!-- 使用相同的内容 -->
          <div class="time-filter">
            <el-radio-group v-model="sortType" size="large">
              <el-radio-button label="latest">最新</el-radio-button>
              <el-radio-button label="hot">最热</el-radio-button>
              <el-radio-button label="favorite">已收藏</el-radio-button>
            </el-radio-group>
          </div>

          <!-- 投票列表 -->
          <div class="vote-grid">
            <template v-if="filteredVotes.length">
              <el-card v-for="vote in filteredVotes"
                       :key="vote.id"
                       class="vote-card"
                       @click="$router.push(`/votes/${vote.id}`)">
                <template #header>
                  <div class="card-header">
                    <h2>{{ vote.title }}</h2>
                    <el-button
                      type="primary"
                      link
                      @click.stop="toggleFavorite(vote.id)"
                      :class="{ 'is-favorite': isFavorite(vote.id) }"
                      class="favorite-btn"
                    >
                      <el-icon>
                        <StarFilled v-if="isFavorite(vote.id)" />
                        <Star v-else />
                      </el-icon>
                    </el-button>
                  </div>
                </template>
                <div class="vote-info">
                  <p>{{ vote.description }}</p>
                  <div class="vote-stats">
                    <span>总票数: {{ vote.totalVotes }}</span>
                    <span>截止时间: {{ formatDate(vote.endTime) }}</span>
                  </div>
                </div>
              </el-card>
            </template>
            <el-empty v-else description="暂无数据" />
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <el-button
      type="primary"
      class="floating-create-btn"
      size="large"
      @click="$router.push('/votes/create')"
    >
      <el-icon><Plus /></el-icon>
      创建投票
    </el-button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useVoteStore } from '@/stores/vote'
import { useUserStore } from '@/stores/user'
import { Star, StarFilled, Plus } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { VOTE_CATEGORIES, getCategoryLabel } from '@/constants/categories'

const voteStore = useVoteStore()
const userStore = useUserStore()
const activeCategory = ref('all')
const sortType = ref('latest')

onMounted(async () => {
  await voteStore.fetchVoteList()
  await voteStore.fetchUserFavorites()
})

const categories = [
  { label: '娱乐', value: 'entertainment' },
  { label: '科技', value: 'tech' },
  { label: '生活', value: 'life' },
  { label: '美食', value: 'food' },
  { label: '体育', value: 'sports' },
  { label: '教育', value: 'education' },
  { label: '时尚', value: 'fashion' },
  { label: '游戏', value: 'games' },
  { label: '旅游', value: 'travel' },
  { label: '音乐', value: 'music' },
  { label: '电影', value: 'movie' },
  { label: '宠物', value: 'pets' }
]

const filteredVotes = computed(() => {
  let votes = voteStore.voteList

  // 添加调试日志
  console.log('当前分类:', activeCategory.value)
  console.log('原始数据:', votes)

  // 先按分类筛选
  if (activeCategory.value !== 'all') {
    votes = votes.filter(vote => {
      // 添加调试日志
      console.log('检查投票:', vote.title, '分类:', vote.category)
      return vote.category === activeCategory.value
    })
  }

  console.log('筛选后数据:', votes)

  // 再按排序方式处理
  let result
  switch (sortType.value) {
    case 'latest':
      result = [...votes].sort((a, b) => new Date(b.createTime) - new Date(a.createTime))
      break
    case 'hot':
      result = [...votes].sort((a, b) => b.totalVotes - a.totalVotes)
      break
    case 'favorite':
      result = votes.filter(vote => voteStore.isFavorite(vote.id))
      break
    default:
      result = votes
  }

  console.log('最终结果:', result)
  return result
})

const formatDate = (date) => {
  return new Date(date).toLocaleDateString()
}

const toggleFavorite = async (voteId) => {
  if (!userStore.isLoggedIn) {
    ElMessage.warning('请先登录')
    return
  }
  try {
    await voteStore.toggleFavorite(voteId)
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
  }
}

const isFavorite = (voteId) => {
  return voteStore.isFavorite(voteId)
}

// 修改标签切换的处理函数
const handleTabChange = (tab) => {
  console.log('标签切换:', tab)
  // 移除重新获取数据的调用，因为 filteredVotes 计算属性会自动处理筛选
  // voteStore.fetchVoteList() // 删除这行
}
</script>

<style scoped>
.title {
  font-size: 48px;
  margin: 10px 0; /* 调整上下间距 */
  padding-top: 30px;
  font-weight: 600; /* 加粗标题 */
}
.subtitle {
  font-size: 24px;
  opacity: 0.9; /* 稍微降低透明度 */
}
.banner {
  height: 300px;
  background-image: linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url('https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?ixlib=rb-4.0.3');
  background-size: cover;
  background-position: center;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -60px;
}

.banner-content {
  text-align: center;
  color: white;
  padding-top: 60px;
}


.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.time-filter {
  margin: 20px 0;
  text-align: center;
}

.vote-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 20px;
  min-height: 200px;
  position: relative;
}

.vote-card {
  cursor: pointer;
  transition: transform 0.2s;
}

.vote-card:hover {
  transform: translateY(-5px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.is-favorite {
  color: #f7ba2a !important;
}

.is-favorite:hover {
  color: #e6a23c !important;
}

.vote-stats {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  color: #666;
  font-size: 0.9em;
}

.floating-create-btn {
  position: fixed;
  top: 50%;
  right: 40px;
  transform: translateY(-50%);
  z-index: 1000;
  border-radius: 25px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 12px 24px;
  transition: all 0.3s ease;
}

.floating-create-btn:hover {
  transform: translateY(-50%) translateX(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

@media (max-width: 768px) {
  .banner {
    height: 200px;
  }

  .item-main {
    flex-direction: column;
    gap: 10px;
  }

  .item-image {
    width: 100%;
    height: 160px;
  }

  .item-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .floating-create-btn {
    right: 20px;
    padding: 10px 20px;
  }
}

/* 基础样式 */
.favorite-btn {
  font-size: 20px;
  color: #909399;
}

/* 未收藏状态 */
.favorite-btn {
  color: #909399;
}

/* 收藏状态 */
.favorite-btn.is-favorite {
  color: #f7ba2a !important;
}

/* hover效果 */
.favorite-btn.is-favorite:hover {
  color: #e6a23c !important;
}

/* 确保样式能覆盖 Element Plus 的默认样式 */
:deep(.favorite-btn.is-favorite) {
  color: #f7ba2a !important;
}

:deep(.favorite-btn.is-favorite:hover) {
  color: #e6a23c !important;
}

/* 添加空状态样式 */
:deep(.el-empty) {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
}
</style>
